import {
  ORDER_STAGE_LABELS,
  ORDER_STAGE_TONES,
  describeJourney,
  formatDuration,
  journeyShares,
  type JourneySegment,
  type Order,
  type OrderJourney,
} from "@estelle/domain";
import { useQuery } from "@tanstack/react-query";
import { Flag } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { dateTime } from "@/modules/orders/format";
import { OrderStageBadge } from "@/modules/orders/screens/StageParts";
import { STAGE_TONE_CLASS } from "@/modules/orders/stageTones";

/**
 * "View journey" (#682): how long one order spent in each stage, as a
 * horizontal bar and the table behind it.
 *
 * The segments arrive worked out (`journeySegments` in `packages/domain`,
 * through `data.stages.journey`), so the bar, the table and any list that
 * shows a journey later read the same figures. Colours are the stage badge's
 * own tones, so the bar, the badge and the table agree.
 *
 * The bar is one `role="img"` that reads out every segment; the table under it
 * is the accessible equivalent, and each of its rows lights up the matching
 * segment and says its dates, so a keyboard user gets what a mouse user gets
 * by hovering. A tap on a segment does the same on a phone.
 *
 * Dashed segments are reconstructed (scans and tags of an order that pre-dates
 * the stage history), not measured, and say "about".
 */

/** Below this share of the bar a segment is too narrow for its name. */
const LABEL_MIN_SHARE = 0.14;

const RECONSTRUCTED =
  "Reconstructed from tracking scans and tags, so the time is approximate.";

/** Small text link that opens the journey, for the order header and the Timeline tab. */
export function ViewJourneyLink({
  order,
  className,
}: {
  order: Pick<Order, "id" | "orderNumber" | "placedAt" | "stage">;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
        }}
        className={cn(
          "text-primary focus-visible:ring-ring min-h-6 rounded-sm text-sm underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:outline-none max-md:min-h-11",
          className,
        )}
      >
        View journey
      </button>
      {open ? (
        <JourneyDialog
          order={order}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/** A dialog on a desktop, a sheet rising from the bottom on a phone. */
function JourneyDialog({
  order,
  onClose,
}: {
  order: Pick<Order, "id" | "orderNumber" | "placedAt" | "stage">;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const title = `Journey of ${order.orderNumber}`;
  const description = "How long the order spent in each stage";
  const body = <JourneyBody order={order} />;
  const onOpenChange = (open: boolean) => {
    if (!open) onClose();
  };
  return isMobile ? (
    <Sheet open onOpenChange={onOpenChange}>
      <SheetContent side="bottom" title={title} description={description}>
        <div className="min-h-0 flex-1 overflow-auto px-4 py-3">{body}</div>
      </SheetContent>
    </Sheet>
  ) : (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent
        title={title}
        description={description}
        className="max-w-2xl"
      >
        {body}
      </DialogContent>
    </Dialog>
  );
}

function JourneyBody({
  order,
}: {
  order: Pick<Order, "id" | "placedAt" | "stage">;
}) {
  const data = useDataSource();
  const journey = useQuery({
    queryKey: ["orders", "journey", order.id, order.stage],
    queryFn: () => data.stages.journey(order.id),
  });

  if (journey.isPending) {
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }
  if (journey.isError) {
    return (
      <p role="alert" className="text-destructive text-sm">
        The journey could not be loaded. Close this and try again.
      </p>
    );
  }
  if (journey.data.segments.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No stage history has been recorded for this order yet.
      </p>
    );
  }
  return <Journey journey={journey.data} />;
}

/** Entered on 12 Oct 2026, 4:10 pm; left on ... */
function span(segment: JourneySegment) {
  return (
    <>
      {dateTime(segment.enteredAt)}
      {segment.leftAt ? <> to {dateTime(segment.leftAt)}</> : null}
    </>
  );
}

function lengthOf(segment: JourneySegment): string {
  if (segment.final) return "Ends here";
  const text = formatDuration(segment.seconds);
  return `${segment.approximate ? "about " : ""}${text}${segment.leftAt === null ? " so far" : ""}`;
}

function Journey({ journey }: { journey: OrderJourney }) {
  const { segments } = journey;
  const [active, setActive] = useState<number | null>(null);
  const { shares, floored } = journeyShares(segments);
  const last = segments[segments.length - 1];
  const open = journey.endedAt === null;
  const ended = last?.final ? last : null;
  const anyApproximate = segments.some((s) => s.approximate);
  const activeSegment = active === null ? undefined : segments[active];
  // A segment too narrow for its name is named in a legend under the bar.
  const unlabelled = [
    ...new Set(
      segments.flatMap((segment, i) => {
        const share = shares[i] ?? 0;
        return share > 0 && share < LABEL_MIN_SHARE ? [segment.stage] : [];
      }),
    ),
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm">
        <span className="font-medium">
          {ended
            ? `Placed to ${ORDER_STAGE_LABELS[ended.stage].toLowerCase()}: `
            : "Placed to now: "}
          {formatDuration(journey.totalSeconds)}
        </span>
        {open ? (
          <span className="text-muted-foreground"> so far, still open</span>
        ) : null}
      </p>

      <div className="space-y-1.5">
        <div
          role="img"
          aria-label={describeJourney(journey)}
          className="flex h-9 w-full gap-0.5 overflow-hidden rounded-md"
        >
          {segments.map((segment, i) => {
            const share = shares[i] ?? 0;
            if (share === 0) return null;
            const isOpenEnd = open && i === segments.length - 1;
            return (
              <div
                key={`${segment.stage}-${segment.enteredAt}`}
                data-active={active === i || undefined}
                onMouseEnter={() => {
                  setActive(i);
                }}
                onMouseLeave={() => {
                  setActive(null);
                }}
                onClick={() => {
                  setActive(active === i ? null : i);
                }}
                style={{ width: `${String(share * 100)}%` }}
                className={cn(
                  "flex min-w-0 cursor-default items-center border px-2 text-xs font-medium transition-[filter]",
                  STAGE_TONE_CLASS[ORDER_STAGE_TONES[segment.stage]],
                  segment.approximate
                    ? "border-dashed border-current/60"
                    : "border-transparent",
                  "data-[active]:brightness-95 dark:data-[active]:brightness-125",
                  isOpenEnd &&
                    "[clip-path:polygon(0_0,calc(100%-10px)_0,100%_50%,calc(100%-10px)_100%,0_100%)] pr-3",
                )}
              >
                {share >= LABEL_MIN_SHARE ? (
                  <span className="truncate">
                    {ORDER_STAGE_LABELS[segment.stage]}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>

        {unlabelled.length > 0 ? (
          <ul
            aria-hidden
            className="text-muted-foreground flex flex-wrap gap-x-3 gap-y-1 text-xs"
          >
            {unlabelled.map((stage) => (
              <li key={stage} className="flex items-center gap-1.5">
                <span
                  className={cn(
                    "size-2.5 rounded-sm border",
                    STAGE_TONE_CLASS[ORDER_STAGE_TONES[stage]],
                  )}
                />
                {ORDER_STAGE_LABELS[stage]}
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex min-h-5 items-start justify-between gap-3 text-xs">
          <p className="text-muted-foreground min-w-0" aria-live="polite">
            {activeSegment ? (
              <>
                <span className="text-foreground font-medium">
                  {ORDER_STAGE_LABELS[activeSegment.stage]}
                </span>
                {" · "}
                {lengthOf(activeSegment)}
                {" · "}
                {span(activeSegment)}
                {activeSegment.approximate ? ` · ${RECONSTRUCTED}` : ""}
                {activeSegment.manualReason
                  ? ` · Set by hand: ${activeSegment.manualReason}`
                  : ""}
              </>
            ) : (
              "Hover, tap or tab to a row to see a stage's dates."
            )}
          </p>
          {ended ? (
            <p className="text-muted-foreground flex shrink-0 items-center gap-1">
              <Flag aria-hidden className="size-3.5" />
              {ORDER_STAGE_LABELS[ended.stage]}{" "}
              {formatDuration(journey.totalSeconds)} after placing
            </p>
          ) : null}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">Time spent in each stage</caption>
          <thead>
            <tr className="text-muted-foreground border-b text-left text-xs">
              <th scope="col" className="py-1.5 pr-3 font-medium">
                Stage
              </th>
              <th scope="col" className="py-1.5 pr-3 font-medium">
                Entered
              </th>
              <th scope="col" className="py-1.5 text-right font-medium">
                Time in stage
              </th>
            </tr>
          </thead>
          <tbody>
            {segments.map((segment, i) => (
              <tr
                key={`${segment.stage}-${segment.enteredAt}`}
                tabIndex={0}
                aria-label={`${ORDER_STAGE_LABELS[segment.stage]}, ${lengthOf(segment)}`}
                data-active={active === i || undefined}
                onFocus={() => {
                  setActive(i);
                }}
                onBlur={() => {
                  setActive(null);
                }}
                onMouseEnter={() => {
                  setActive(i);
                }}
                onMouseLeave={() => {
                  setActive(null);
                }}
                className="focus-visible:ring-ring data-[active]:bg-muted/60 border-b last:border-b-0 focus-visible:ring-2 focus-visible:outline-none"
              >
                <td className="py-2 pr-3 align-top">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <OrderStageBadge stage={segment.stage} />
                    {segment.manualReason !== null ? (
                      <span
                        title={`Set by hand: ${segment.manualReason}`}
                        className="text-muted-foreground border-border rounded-sm border px-1 text-[0.6875rem] leading-4 font-medium"
                      >
                        manual
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="text-muted-foreground py-2 pr-3 align-top text-xs">
                  {dateTime(segment.enteredAt)}
                </td>
                <td
                  className="py-2 text-right align-top whitespace-nowrap tabular-nums"
                  title={segment.approximate ? RECONSTRUCTED : undefined}
                >
                  {lengthOf(segment)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t">
              <th
                scope="row"
                colSpan={2}
                className="py-2 text-left font-medium"
              >
                {ended
                  ? `Placed to ${ORDER_STAGE_LABELS[ended.stage].toLowerCase()}`
                  : "Placed to now"}
              </th>
              <td className="py-2 text-right font-medium whitespace-nowrap tabular-nums">
                {formatDuration(journey.totalSeconds)}
                {open ? " so far" : ""}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="text-muted-foreground space-y-1 text-xs">
        {segments.length === 1 ? (
          <p>Journey starts when the order was placed.</p>
        ) : null}
        {anyApproximate ? (
          <p>
            Dashed segments are reconstructed from tracking scans and tags, so
            their times are approximate.
          </p>
        ) : null}
        {floored ? (
          <p>Not to scale: short stages are widened so they stay visible.</p>
        ) : null}
      </div>
    </div>
  );
}
